/**
 * 路由配置
 *
 * 路由结构：
 *  - /login               登录页（auth 布局）
 *  - /dashboard           仪表盘（default 布局，需 dashboard）
 *  - /inspection-plans    检验计划管理（default 布局，需 inspection-plans:list）
 *  - /quality-records     质量检验记录（default 布局，需 quality-records:list）
 *  - /defects             缺陷管理（default 布局，需 defects:list）
 *  - /nonconformances     不合格品管理（default 布局，需 nonconformances:list）
 *  - /capa                CAPA 纠正预防措施（default 布局，需 capa:list）
 *  - /suppliers           供应商管理（default 布局，需 suppliers:list）
 *  - /audits              审核管理（default 布局，需 audits:list）
 *  - /mobile              移动端首页（default 布局，需登录）
 *  - /mobile/scan-inspection 扫码检验（default 布局，需登录）
 *  - /mobile/defect-report 缺陷上报（default 布局，需登录）
 *  - /system/users        用户管理（default 布局，需 system:users）
 *  - /system/audit-logs   审计日志（default 布局，需 system:audit-logs）
 *  - /403                 无权限页
 *  - /:pathMatch(.*)*     404 页
 *
 * 全局守卫：
 *  - beforeEach: 检查登录状态与页面权限
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// 路由元信息类型扩展
declare module 'vue-router' {
  interface RouteMeta {
    /** 页面标题 */
    title?: string;
    /** 是否需要登录 */
    requiresAuth?: boolean;
    /** 所需权限列表（满足任一即可） */
    permissions?: string[];
    /** 使用的布局 */
    layout?: 'default' | 'auth';
    /** 是否在侧边栏隐藏 */
    hidden?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login.vue'),
    meta: { title: '登录', requiresAuth: false, layout: 'auth', hidden: true },
  },
  {
    path: '/sso/callback',
    name: 'sso-callback',
    component: () => import('@/views/sso-callback.vue'),
    meta: { title: 'SSO 登录', requiresAuth: false, hidden: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/default.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '仪表盘', requiresAuth: true, permissions: ['dashboard'] },
      },
      {
        path: 'documents',
        name: 'documents',
        component: () => import('@/views/documents/index.vue'),
        meta: { title: '图文档', requiresAuth: true, permissions: ['document:list'] },
      },
      {
        path: 'inspection-plans',
        name: 'inspection-plans',
        component: () => import('@/views/inspection-plans/index.vue'),
        meta: { title: '检验计划', requiresAuth: true, permissions: ['inspection-plans:list'] },
      },
      {
        path: 'quality-records',
        name: 'quality-records',
        component: () => import('@/views/quality-records/index.vue'),
        meta: { title: '质量记录', requiresAuth: true, permissions: ['quality-records:list'] },
      },
      {
        path: 'defects',
        name: 'defects',
        component: () => import('@/views/defects/index.vue'),
        meta: { title: '缺陷管理', requiresAuth: true, permissions: ['defects:list'] },
      },
      {
        path: 'nonconformances',
        name: 'nonconformances',
        component: () => import('@/views/nonconformances/index.vue'),
        meta: { title: '不合格品', requiresAuth: true, permissions: ['nonconformances:list'] },
      },
      {
        path: 'capa',
        name: 'capa',
        component: () => import('@/views/capa/index.vue'),
        meta: { title: 'CAPA', requiresAuth: true, permissions: ['capa:list'] },
      },
      {
        path: 'suppliers',
        name: 'suppliers',
        component: () => import('@/views/suppliers/index.vue'),
        meta: { title: '供应商管理', requiresAuth: true, permissions: ['suppliers:list'] },
      },
      {
        path: 'audits',
        name: 'audits',
        component: () => import('@/views/audits/index.vue'),
        meta: { title: '审核管理', requiresAuth: true, permissions: ['audits:list'] },
      },
      {
        path: 'system/users',
        name: 'system-users',
        component: () => import('@/views/system/users.vue'),
        meta: { title: '用户管理', requiresAuth: true, permissions: ['system:users'] },
      },
      {
        path: 'system/audit-logs',
        name: 'system-audit-logs',
        component: () => import('@/views/system/audit-logs.vue'),
        meta: { title: '审计日志', requiresAuth: true, permissions: ['system:audit-logs'] },
      },
      {
        path: 'mobile',
        name: 'mobile-home',
        component: () => import('@/views/mobile/index.vue'),
        meta: { title: '移动作业', requiresAuth: true, hidden: true },
      },
      {
        path: 'mobile/scan-inspection',
        name: 'mobile-scan-inspection',
        component: () => import('@/views/mobile/scan-inspection.vue'),
        meta: { title: '扫码检验', requiresAuth: true, hidden: true },
      },
      {
        path: 'mobile/defect-report',
        name: 'mobile-defect-report',
        component: () => import('@/views/mobile/defect-report.vue'),
        meta: { title: '缺陷上报', requiresAuth: true, hidden: true },
      },
    ],
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/error/403.vue'),
    meta: { title: '无权限', requiresAuth: false, hidden: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '页面不存在', requiresAuth: false, hidden: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // 切换路由时滚动到顶部
  scrollBehavior() {
    return { top: 0 };
  },
});

// 标记是否已初始化用户信息，避免重复请求
let userInitialized = false;

/**
 * 全局前置守卫
 * 1. 未登录访问需认证页面 -> 跳转 /login
 * 2. 已登录访问登录页 -> 跳转 /dashboard
 * 3. 无权限访问受限页面 -> 跳转 /403
 */
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - QMS 质量管理系统`;
  }

  // 如果需要登录但未登录，跳转到登录页
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // 如果有 token 但还没有 user 信息（页面刷新场景），尝试获取
    if (authStore.token && !userInitialized) {
      userInitialized = true;
      try {
        await authStore.fetchUserInfo();
      } catch {
        // 获取用户信息失败，跳转登录
        return next({ name: 'login', query: { redirect: to.fullPath } });
      }
    } else {
      return next({ name: 'login', query: { redirect: to.fullPath } });
    }
  }

  // 已登录用户访问登录页，跳转到首页
  if (to.name === 'login' && authStore.isAuthenticated) {
    return next({ name: 'dashboard' });
  }

  // 权限检查
  if (to.meta.permissions && to.meta.permissions.length > 0) {
    const hasPermission = to.meta.permissions.some((perm) => authStore.hasPermission(perm));
    if (!hasPermission) {
      return next({ name: 'forbidden' });
    }
  }

  next();
});

export default router;
